import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { HiOutlineXMark } from "react-icons/hi2";
import safetyObservationService from "../../../services/safetyObservationService";
import plantService from "../../../services/plantService";
import masterDataService from "../../../services/masterDataService";
import userService from "../../../services/userService";
import CustomMultiSelect from "./CustomMultiSelect";
import "./Schedule.css";
import SharedModal from "../../../components/common/SharedModal";

const CreateSchedule = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    date: "",
    endDate: "",
    startTime: "",
    duration: "",
    plantId: "",
    frequency: "",
    assignmentType: "users", // New field: 'users' or 'role'
    assignToUsers: [],
    assignRole: "",
    departmentId: "",
  });

  const [facilities, setFacilities] = useState([]);

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const tenantId = localStorage.getItem("tenantId") || 0;
      const plantsData = await plantService.getAllPlants();
      setFacilities(plantsData || []);

      const usersData = await userService.getAllUsers();
      if (usersData) {
        setUsers(
          usersData.map((u) => ({
            label: u.fullName || u.username,
            value: u.fullName || u.username,
          })),
        );
      }

      const deptsData = await masterDataService.getDepartments(tenantId);
      if (deptsData) {
        setDepartments(deptsData);
      }

      const rolesData = await masterDataService.getRoles(tenantId);
      if (rolesData) {
        setRoles(rolesData);
      }
    } catch (error) {
      console.error("Error loading master data:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === "plantId") {
        newData.departmentId = "";
      }
      if (name === "assignmentType") {
        if (value === "role") newData.assignToUsers = [];
        if (value === "users") newData.assignRole = "";
      }
      return newData;
    });
  };

  const frequencyOptions = ["Once", "Daily", "Weekly", "Monthly"];

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (!formData.frequency) {
        toast.warn("Please select a frequency");
        return;
      }
      if (!formData.date || !formData.startTime) {
        toast.warn("Please select a valid Date and Start Time");
        return;
      }

      setSubmitting(true);
      const payload = {
        ...formData,
        assignToUsers: Array.isArray(formData.assignToUsers)
          ? formData.assignToUsers.join(", ")
          : formData.assignToUsers,
      };
      await safetyObservationService.createScheduleConfig(payload);
      toast.success("Schedule configuration saved successfully!");
      onClose();
    } catch (err) {
      console.error("Error saving schedule:", err);
      toast.error("Failed to save schedule. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setFormData({
      date: "",
      endDate: "",
      startTime: "",
      duration: "",
      plantId: "",
      frequency: "",
      assignmentType: "users",
      assignToUsers: [],
      assignRole: "",
      departmentId: "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title="New Schedule"
      customClass="schedule-modal"
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCloseModal}
            disabled={submitting}
          >
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={submitting}>
            {submitting ? "Creating..." : "Create Schedule"}
          </button>
        </>
      }
    >
      <div className="shared-modal-body">
          <form onSubmit={handleSave} className="modal-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="plantId" className="form-label">Plant</label>
                <select
                  id="plantId"
                  name="plantId"
                  value={formData.plantId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Plant</option>
                  {facilities.map((f, i) => (
                    <option key={i} value={f.plantId || f.id}>
                      {f.plantName || f.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="departmentId" className="form-label">Department</label>
                <select
                  id="departmentId"
                  name="departmentId"
                  value={formData.departmentId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Department</option>
                  {departments.map((d) => (
                    <option
                      key={d.departmentId || d.id}
                      value={d.departmentId || d.id}
                    >
                      {d.departmentName || d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date" className="form-label required">Schedule Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="endDate" className="form-label">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startTime" className="form-label required">Start Time</label>
                <input
                  type="time"
                  id="startTime"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="duration" className="form-label">Duration (mins)</label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  placeholder="30"
                  value={formData.duration}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="frequency" className="form-label required">Frequency / How Often</label>
              <select
                id="frequency"
                name="frequency"
                value={formData.frequency}
                onChange={handleInputChange}
              >
                <option value="">Select Frequency</option>
                {frequencyOptions.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Assignment Basis</label>
              <div className="assignment-toggle" style={{
                  display: "flex",
                  background: "#f1f5f9",
                  padding: "4px",
                  borderRadius: "12px",
                  marginBottom: "8px",
                }}>
                <button
                  type="button"
                  className={`toggle-btn ${formData.assignmentType === "users" ? "active" : ""}`}
                  onClick={() =>
                    handleInputChange({
                      target: { name: "assignmentType", value: "users" },
                    })
                  }
                  style={{
                    flex: 1,
                    padding: "8px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    background: formData.assignmentType === "users" ? "#fff" : "transparent",
                    color: formData.assignmentType === "users" ? "#6366f1" : "#64748b",
                    boxShadow: formData.assignmentType === "users" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                  }}
                >
                  Specific Users
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${formData.assignmentType === "role" ? "active" : ""}`}
                  onClick={() =>
                    handleInputChange({
                      target: { name: "assignmentType", value: "role" },
                    })
                  }
                  style={{
                    flex: 1,
                    padding: "8px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    background: formData.assignmentType === "role" ? "#fff" : "transparent",
                    color: formData.assignmentType === "role" ? "#6366f1" : "#64748b",
                    boxShadow: formData.assignmentType === "role" ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                  }}
                >
                  By Role
                </button>
              </div>
            </div>

            {formData.assignmentType === "users" ? (
              <div className="form-group">
                <label htmlFor="assignToUsers" className="form-label">Assign to Users</label>
                <CustomMultiSelect
                  id="assignToUsers"
                  name="assignToUsers"
                  label="Select Users"
                  options={users}
                  selectedValues={formData.assignToUsers || []}
                  onChange={handleInputChange}
                />
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="assignRole" className="form-label">Assign Role</label>
                <select
                  id="assignRole"
                  name="assignRole"
                  value={formData.assignRole}
                  onChange={handleInputChange}
                >
                  <option value="">Select Role</option>
                  {roles.map((r) => (
                    <option key={r.roleId || r.id} value={r.roleId || r.id}>
                      {r.roleName || r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

          </form>
      </div>
    </SharedModal>
  );
};

export default CreateSchedule;
